import { Event } from '@custom-types/activity'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'
import ActivitySourceEventsIterator, {
  EventsResp,
} from '@utils/iterators/ActivitySourceEvents.iterator'

/**
 * Handles reading events from the user's activity stream. The `next` method will retrieve the next
 * event, or null if there are no more events. Note that the `next` method is actually
 * asynchronous, it must be passed a callback function.
 *
 * Internally, this class is actually incrementally loading the list of sources, and also
 * incrementally loading the events for each source, and combining the various event lists in a
 * sorted manner.
 */
export default class ActivityStreamEventsIterator {
  private readonly options: Options

  /**
   * The sources, ordered from newest next event to oldest, and the mapping from source to name. IF
   * this is empty, it either means that we haven't yet gone to the server to get the list of
   * sources, or it could mean that there are no more sources to retrieve, depending on the
   * `sourcesRetrieved` flag below
   */
  private sources: Source[]
  private sourcesByName: { [key: string]: Source | null }

  /** Flag indicating if we have yet gone to the server to get the initial list of sources */
  private sourcesRetrieved: boolean

  /**
   * The sources that have exhausted all of their events, indexed by name. This is used to handle
   * the case where sources "show up" again because they have generated new events.
   */
  private completedSourcesByName: { [key: string]: Source | null }

  /** The events that have already been returned, indexed by ID - used to prevent duplicates */
  private eventsById: { [key: string]: Event }

  private sourcesNextBeforeId: string | null
  private sourcesNextAfterId: string | null

  /** The oldest event ID that has been returned - returned events must be older than this */
  private oldestReturnedEventId: string | null

  /** Used to temporarily store the callback passed to `next()` */
  private nextCallback: (() => void) | null

  /**
   * When an event has been retrieved, its source is set here. On the next call to `next`, it will
   * first deal with refreshing the source.
   */
  private sourceToRefresh: Source | null

  /**
   * Keeps track of whether the user's personal source has been loaded yet - only used if the
   * options includes `personalSource`.
   */
  private personalSourceLoaded: boolean

  /** Once stop is called, the iterator will make no more calls to the callback function */
  private stopped: boolean

  /**
   * @param opts The configuration options
   * @param opts.personalSource If this is set, then the given source is mixed into the list of
   *  sources (effectively mixing events done by sources the user is following and events done to
   *  the user's scores)
   * @param opts.getMoreEventsCall Function used to override the SourceIterator's natural call
   *  to get more events from a source (used by testing during mocking)
   * @param opts.getMoreSourcesCall Function used to override the natural call to get more sources
   *  for the user (used by testing during mocking)
   */
  constructor(opts?: Options) {
    this.options = opts ?? {}
    this.sources = []
    this.sourcesByName = {}
    this.sourcesRetrieved = false
    this.completedSourcesByName = {}
    this.eventsById = {}
    this.sourcesNextBeforeId = null
    this.sourcesNextAfterId = null
    this.oldestReturnedEventId = null
    this.nextCallback = null
    this.sourceToRefresh = null
    this.personalSourceLoaded = false
    this.stopped = false
  }

  stop(): void {
    this.stopped = true
  }

  /**
   * Fetches the next event from the stream. When the event has been retrieved, the callback
   * function is called and the event is passed to it. If there are no more events, then the
   * callback function is called with null passed to it. Note that this process is NOT re-entrant -
   * once next is called, next must NOT be called again until the callback has been called.
   */
  async next(): Promise<Event | null> {
    if (this.stopped) return null

    // Handle the personal source if it was requested
    if (this.options.personalSource && !this.personalSourceLoaded) {
      return this.loadPersonalSource()
    }

    // See if a source was used in the previous call, and now needs to be refreshed and re-sorted
    // into the list before continuing
    if (this.sourceToRefresh) {
      const source = this.sourceToRefresh
      this.sourceToRefresh = null
      return this.prepareNextEvent(source)
    }

    // If we haven't yet retrieved any sources, do so now
    if (!this.sourcesRetrieved) {
      return this.retrieveSources()
    }

    // In the smoothest case, we look at the first source in the sorted list, get its next event
    // and return it
    if (this.sources.length > 0) {
      const source = this.sources[0]
      const { nextEvent } = source
      if (nextEvent) {
        this.sourceToRefresh = source

        // Make sure the event is a valid one to show
        if (this.isValidEvent(nextEvent)) {
          this.oldestReturnedEventId = nextEvent.event_id
          this.eventsById[nextEvent.event_id] = nextEvent
          return nextEvent
        }

        // Otherwise, go back into `next` - because `sourcesToRefresh` has been set, this will cause
        // that source to get its next event
        return this.next()
      }

      // If we haven't yet gotten the first event, then run this source through the "refresh"
      // procedures
      return this.prepareNextEvent(source)
    }

    // If there are no more sources in our sorted list, then we may need to get more
    if (this.sourcesNextBeforeId) {
      return this.retrieveSources()
    }

    // Otherwise, if after all this we are out of sources, then we are done
    return null
  }

  /**
   * Called to load the personal source, to mix it into the list of sources
   */
  private async loadPersonalSource(): Promise<Event | null> {
    if (!this.options.personalSource) {
      return null
    }

    this.personalSourceLoaded = true

    const personalSource = this.addSource({ source_name: this.options.personalSource })
    if (!personalSource) return null
    return this.prepareNextEvent(personalSource)
  }

  /** Retrieves the next set of sources from the server */
  private async retrieveSources(): Promise<Event | null> {
    const batchSize = 10
    let data: ActivitySourcesResponse | null = null
    if (typeof this.options.getMoreSourcesCall === 'function') {
      data = await this.options.getMoreSourcesCall(
        this.sourcesNextBeforeId,
        this.sourcesNextAfterId,
        batchSize
      )
    } else {
      const [resp] = await http.post(ApiRoutes.activityStream.sourcesPath(), {
        before_id: this.sourcesNextBeforeId,
        after_id: this.sourcesNextAfterId,
        limit: batchSize,
      })

      if (resp?.data) {
        data = resp.data
      }
    }

    if (!data) {
      return null
    }
    return this.sourcesReceived(data)
  }

  /** Called when a new set of sources has been retrieved from the server */
  private async sourcesReceived(data: ActivitySourcesResponse): Promise<Event | null> {
    this.sourcesNextBeforeId = data.next_before_id
    this.sourcesNextAfterId = data.next_after_id
    this.sourcesRetrieved = true

    // If next_before_id is null, that means we've run out of sources
    if (!data.next_before_id) {
      // If we have no more sources locally, then we're done
      if (this.sources.length === 0) {
        return null
      }
      // Otherwise, go back into next and try again
      return this.next()
    }

    // Go through all of the reveived sources
    let sources: SourceResponse[] = []
    if (data.after_sources) sources = sources.concat(data.after_sources)
    if (data.before_sources) sources = sources.concat(data.before_sources)
    for (const source of sources) {
      this.addSource(source)
    }

    // Sort the list of sources
    return this.sortSources()
  }

  /**
   * This will prepare the next event for the given source, which might happen when the source is
   * being used for the first time, or after the source's next event has been used.
   * @param source
   */
  private async prepareNextEvent(source: Source): Promise<Event | null> {
    const nextEvent = await source.eventsIterator.next()
    return this.nextEventReceived(nextEvent, source)
  }

  /** Called when the next event has been received for a source */
  private async nextEventReceived(event: Event | null, source: Source): Promise<Event | null> {
    if (event) {
      // Set the next event, then re-sort the source into the list - note that it's possible that
      // this event will not be a valid one to show (might be a duplicate of some other event, or
      // newer than the oldest shown event) - however, that will be caught in the main `next` call
      // by `isValidEvent`.
      source.nextEvent = event
      source.nextEventId = event.event_id
      return this.sortSources()
    }
    return this.removeSource(source)
  }

  /**
   * Adds the given source into the list (or updates it if it's already there)
   * @private
   */
  private addSource(source: SourceResponse): Source | null {
    let ret: Source | null = null

    // If we already have the source, or if the source was already completed, then this means a
    // new event must have appeared for the source, causing it to "jump ahead" in the list of
    // most-recent sources. However, we don't care about those new events, so we basically ignore
    // those sources.
    if (
      !this.sourcesByName[source.source_name] &&
      !this.completedSourcesByName[source.source_name]
    ) {
      // Add it into the list of sources
      ret = new Source(
        source.source_name,
        source.last_activity_event_id,
        source.source_events,
        this.options
      )
      this.sources.push(ret)
      this.sourcesByName[ret.sourceName] = ret
    }
    return ret
  }

  /** Remove a source from the list of sources */
  private async removeSource(source: Source): Promise<Event | null> {
    for (let i = 0; i < this.sources.length; i++) {
      if (this.sources[i] === source) {
        this.sources.splice(i, 1)
        break
      }
    }
    this.sourcesByName[source.sourceName] = null
    this.completedSourcesByName[source.sourceName] = source
    return this.sortSources()
  }

  /**
   * Any time the list of sources changes in some way, we need to make sure it's back in sorted
   * order
   */
  private async sortSources(): Promise<Event | null> {
    this.sources.sort((a, b) => {
      if (a.nextEventId > b.nextEventId) return -1
      if (a.nextEventId < b.nextEventId) return 1
      return 0
    })

    // At this point, the source with the most recent event should be at the front of the list.
    // However, it's possible there's another source we haven't gotten yet that has a more recent
    // event. We can tell by looking at the next_before_id the server gave us the last time we got
    // some sources - if it's greater than the next event we have, then we need to fetch more
    // sources since one of them may have a more recent event
    if (
      this.sources.length > 0 &&
      this.sourcesNextBeforeId &&
      this.sources[0].nextEventId < this.sourcesNextBeforeId
    ) {
      return this.retrieveSources()
    }

    // Otherwise, just continue the process
    return this.next()
  }

  /**
   * Returns true if this is a "valid" event. Events are invalid if the occur after the oldest
   * returned event ID (which would be the case for new events showing up), or if they have an ID
   * that's already been used (possible since multiple sources could return the same event)
   */
  private isValidEvent(event: Event): boolean {
    if (this.oldestReturnedEventId && event.event_id > this.oldestReturnedEventId) {
      return false
    }

    // Check for duplicates
    if (this.eventsById[event.event_id]) {
      return false
    }
    return true
  }
}

/**
 * Inner class that holds information about one Source being tracked. The initial source events is
 * the optional list of events that may have arrived with the source.
 */
export class Source {
  readonly sourceName: string

  /** The iterator for pulling events from this source */
  readonly eventsIterator: ActivitySourceEventsIterator

  /**
   * The ID of the next event available to be used, which is the order used to sort the sources.
   * Once we start pulling events from the iterator, we set this value to the next event's ID.
   */
  nextEventId: string

  /**
   * The next event to be returned by this source - if null, that means no events have yet been
   * read from the source.
   */
  nextEvent: Event | null

  constructor(
    sourceName: string,
    lastActivityEventId?: string,
    initialSourceEvents?: EventsResp,
    opts?: Options
  ) {
    this.sourceName = sourceName
    this.eventsIterator = new ActivitySourceEventsIterator(sourceName, {
      initialSourceEvents,
      getMoreEventsCall: opts?.getMoreEventsCall?.bind(this),
    })
    this.nextEvent = null
    this.nextEventId = lastActivityEventId ?? ''
  }
}

export type Options = {
  personalSource?: string
  getMoreEventsCall?: (
    source_name: string,
    limit: number,
    before_id: string | null
  ) => Promise<EventsResp>
  getMoreSourcesCall?: (
    before_id: string | null,
    after_id: string | null,
    batch_size: number
  ) => Promise<ActivitySourcesResponse>
}

export type ActivitySourcesResponse = {
  before_sources: SourceResponse[]
  after_sources: SourceResponse[]
  next_before_id: string | null
  next_after_id: string | null
}

export type SourceResponse = {
  source_name: string
  last_activity_event_id?: string
  source_events?: EventsResp
}
